import React, { useCallback, useState } from 'react';
import { BarChart2, Save, RefreshCw, Download, Calculator, Shield, AlertTriangle, Car } from 'lucide-react';
import Button from '../../../../../components/ui/Button';

// Импорт секций формы
import CarParametersSection from './CarParametersSection';
import DriverParametersSection from './DriverParametersSection';
import AdditionalParametersSection from './AdditionalParametersSection';
import AdditionalOptionsSection from './AdditionalOptionsSection';

// Импорт утилитарных функций
import { isPositiveNumber, isInteger } from '../../../../../utils/validation';


/**
 * Компонент основной формы расчета ОСАГО
 */
const CalculationForm = ({ 
  formData, 
  setFormData, 
  errors, 
  setErrors, 
  isCalculating, 
  setIsCalculating,
  results,
  setResults,
  breakdown,
  setBreakdown,
  carTypes,
  popularRegions,
  kbmOptions,
  saveCalculation 
}) => {
  
  // Состояние для имени сохраняемого расчета
  const [saveCalculationName, setSaveCalculationName] = useState('');
  const [showSaveDialog, setShowSaveDialog] = useState(false);
  
  // Функция парсинга числа с плавающей точкой или возврат значения по умолчанию
  const parseFloatOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseFloat(String(val).replace(',', '.'));
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Функция парсинга целого числа или возврат значения по умолчанию
  const parseIntOrDefault = (val, defaultVal = NaN) => {
    const parsed = parseInt(String(val), 10);
    return isNaN(parsed) ? defaultVal : parsed;
  };
  
  // Обработчик изменения полей ввода
  const handleInputChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    const fieldValue = type === 'checkbox' ? checked : value;

    setFormData(prev => {
      const newState = { ...prev, [name]: fieldValue };

      // Если включен мультидрайв, сбрасываем массив водителей
      if (name === 'multiDrive' && checked) {
        newState.drivers = [];
      }
      // Если отключен мультидрайв, добавляем первого водителя
      else if (name === 'multiDrive' && !checked && prev.drivers.length === 0) {
        newState.drivers = [{ age: prev.driverAge, experience: prev.drivingExperience, noAccidents: true }];
      }

      // Сбрасываем связанные поля если функция отключена
      if (name === 'hasAccidents' && !checked) {
        newState.accidentsCount = '0';
      }
      
      if (name === 'hasDiscounts' && !checked) {
        newState.kbmDiscount = '1';
      }
      
      if (name === 'hasSeasonality' && !checked) {
        newState.seasonalityMonths = ['6', '7', '8'];
      }
      
      if (name === 'includeAdditionalOptions' && !checked) {
        newState.additionalOptions = {
          extendedCoverage: false,
          noDeductible: false,
          evacuationService: false,
          rentalCar: false,
          remotePolicyIssue: false
        };
      }

      return newState;
    });

    // Очищаем ошибку для изменяемого поля
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  }, [errors, setErrors, setFormData]);

  // Обработчик изменений в массиве водителей
  const handleDriverChange = useCallback((index, field, value) => {
    setFormData(prev => {
      const updatedDrivers = [...prev.drivers];
      updatedDrivers[index] = {
        ...updatedDrivers[index],
        [field]: value
      };
      return {
        ...prev,
        drivers: updatedDrivers
      };
    });
  }, [setFormData]);

  // Добавление нового водителя
  const addDriver = useCallback(() => {
    setFormData(prev => ({
      ...prev,
      drivers: [
        ...prev.drivers,
        { age: '30', experience: '10', noAccidents: true }
      ]
    }));
  }, [setFormData]);

  // Удаление водителя
  const removeDriver = useCallback((index) => {
    setFormData(prev => {
      const updatedDrivers = prev.drivers.filter((_, i) => i !== index);
      return {
        ...prev,
        drivers: updatedDrivers
      };
    });
  }, [setFormData]);

  // Обработчик изменения дополнительных опций
  const handleOptionChange = useCallback((option, checked) => {
    setFormData(prev => ({
      ...prev,
      additionalOptions: {
        ...prev.additionalOptions,
        [option]: checked
      }
    }));
  }, [setFormData]);

  // Обработчик выбора месяцев сезонности
  const handleSeasonalityChange = useCallback((month, checked) => {
    setFormData(prev => {
      const months = [...prev.seasonalityMonths];
      
      if (checked && !months.includes(month)) {
        months.push(month);
      } else if (!checked && months.includes(month)) {
        const index = months.indexOf(month);
        months.splice(index, 1);
      }
      
      return {
        ...prev,
        seasonalityMonths: months
      };
    });
  }, [setFormData]);

  // Валидация формы
  const validateForm = useCallback(() => {
    const newErrors = {};
    const {
      carType, enginePower, regionCode, driverAge, drivingExperience,
      multiDrive, drivers, usagePeriod, hasAccidents, accidentsCount,
      hasDiscounts, kbmDiscount, hasSeasonality, seasonalityMonths, carAgeYears
    } = formData;

    // Парсинг числовых значений
    const numEnginePower = parseFloatOrDefault(enginePower, -1);
    const numDriverAge = parseIntOrDefault(driverAge, -1);
    const numDrivingExperience = parseIntOrDefault(drivingExperience, -1);
    const numUsagePeriod = parseIntOrDefault(usagePeriod, -1);
    const numAccidentsCount = parseIntOrDefault(accidentsCount, -1);
    const numCarAgeYears = parseIntOrDefault(carAgeYears, -1);

    // Валидация параметров автомобиля
    if (!carType) newErrors.carType = 'Выберите тип транспортного средства.';
    
    if (numEnginePower <= 0) newErrors.enginePower = 'Мощность должна быть положительной.';
    if (!newErrors.enginePower && numEnginePower > 1500) newErrors.enginePower = 'Максимальная мощность 1500 л.с.';
    
    if (!regionCode) newErrors.regionCode = 'Выберите регион регистрации.';
    
    if (numCarAgeYears < 0) newErrors.carAgeYears = 'Укажите корректный возраст автомобиля.';
    if (!newErrors.carAgeYears && numCarAgeYears > 50) newErrors.carAgeYears = 'Возраст не может быть более 50 лет.';

    // Валидация параметров водителей
    if (!multiDrive) {
      if (numDriverAge < 18) newErrors.driverAge = 'Возраст не может быть меньше 18 лет.';
      if (!newErrors.driverAge && numDriverAge > 90) newErrors.driverAge = 'Возраст не может быть более 90 лет.';
      
      if (numDrivingExperience < 0) newErrors.drivingExperience = 'Стаж не может быть отрицательным.';
      if (!newErrors.drivingExperience && numDrivingExperience > numDriverAge - 18) {
        newErrors.drivingExperience = `Стаж не может быть больше ${numDriverAge - 18} лет.`;
      }
      
      // Валидация списка водителей
      if (drivers.length === 0) {
        newErrors.drivers = 'Добавьте хотя бы одного водителя.';
      } else {
        const driverErrors = {};
        
        drivers.forEach((driver, index) => {
          const driverAge = parseIntOrDefault(driver.age, -1);
          const driverExperience = parseIntOrDefault(driver.experience, -1);
          
          if (driverAge < 18) {
            driverErrors[`${index}_age`] = 'Возраст не может быть меньше 18 лет.';
          }
          if (driverAge > 90) {
            driverErrors[`${index}_age`] = 'Возраст не может быть более 90 лет.';
          }
          
          if (driverExperience < 0) {
            driverErrors[`${index}_experience`] = 'Стаж не может быть отрицательным.';
          }
          if (driverExperience > driverAge - 18) {
            driverErrors[`${index}_experience`] = `Стаж не может быть больше ${driverAge - 18} лет.`;
          }
        });
        
        if (Object.keys(driverErrors).length > 0) {
          newErrors.driverErrors = driverErrors;
        }
      }
    }

    // Валидация периода использования
    if (numUsagePeriod <= 0) newErrors.usagePeriod = 'Период должен быть положительным.';
    if (!newErrors.usagePeriod && numUsagePeriod > 12) newErrors.usagePeriod = 'Максимальный период - 12 месяцев.';
    
    // Валидация ДТП
    if (hasAccidents) {
      if (numAccidentsCount < 0) newErrors.accidentsCount = 'Количество ДТП не может быть отрицательным.';
      if (!newErrors.accidentsCount && numAccidentsCount > 10) newErrors.accidentsCount = 'Слишком большое количество ДТП.';
    }
    
    // Валидация КБМ
    if (hasDiscounts && (!kbmDiscount || parseFloatOrDefault(kbmDiscount, 0) <= 0)) {
      newErrors.kbmDiscount = 'Выберите корректное значение КБМ.';
    }
    
    // Валидация сезонности
    if (hasSeasonality && (!seasonalityMonths || seasonalityMonths.length === 0)) {
      newErrors.seasonalityMonths = 'Выберите хотя бы один месяц использования.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, setErrors]);

  // Расчет базовой ставки ОСАГО по типу ТС и мощности двигателя
  const calculateBaseRate = (carType, enginePower) => {
    // Базовые ставки по типам ТС и мощности (актуальные на 2025 год)
    const baseRates = {
      passenger: {
        upto50: 2479,
        upto70: 3646,
        upto100: 4648,
        upto120: 5404,
        upto150: 5515,
        above150: 7536
      },
      truck: {
        upto80: 4110,
        upto110: 5284,
        upto150: 6341,
        upto200: 7401,
        upto250: 9503,
        above250: 11431
      },
      bus: {
        upto80: 3509,
        upto110: 4416,
        upto150: 6166,
        above150: 7697
      },
      motorcycle: {
        any: 1407
      },
      tractor: {
        any: 1751
      },
      trailer: {
        any: 1327
      }
    };

    const power = parseFloat(enginePower);
    
    // Определяем базовый тариф по типу ТС и мощности
    switch (carType) {
      case 'passenger':
        if (power <= 50) return baseRates.passenger.upto50;
        if (power <= 70) return baseRates.passenger.upto70;
        if (power <= 100) return baseRates.passenger.upto100;
        if (power <= 120) return baseRates.passenger.upto120;
        if (power <= 150) return baseRates.passenger.upto150;
        return baseRates.passenger.above150;
      
      case 'truck':
        if (power <= 80) return baseRates.truck.upto80;
        if (power <= 110) return baseRates.truck.upto110;
        if (power <= 150) return baseRates.truck.upto150;
        if (power <= 200) return baseRates.truck.upto200;
        if (power <= 250) return baseRates.truck.upto250;
        return baseRates.truck.above250;
      
      case 'bus':
        if (power <= 80) return baseRates.bus.upto80;
        if (power <= 110) return baseRates.bus.upto110;
        if (power <= 150) return baseRates.bus.upto150;
        return baseRates.bus.above150;
      
      case 'motorcycle':
        return baseRates.motorcycle.any;
      
      case 'tractor':
        return baseRates.tractor.any;
      
      case 'trailer':
        return baseRates.trailer.any;
      
      default:
        return baseRates.passenger.upto100;
    }
  };

  // Получение коэффициента региона
  const getRegionFactor = (regionCode) => {
    const region = popularRegions.find(r => r.code === regionCode);
    return region ? region.factor : 1.0;
  };

  // Расчет коэффициента возраста и стажа
  const calculateAgeExperienceFactor = (age, experience) => {
    // Таблица коэффициентов по возрасту и стажу (актуальная на 2025 год)
    const ageExperienceFactors = [
      // [мин_возраст, макс_возраст, мин_стаж, макс_стаж, коэффициент]
      [16, 21, 0, 3, 1.93],
      [22, 24, 0, 3, 1.79],
      [25, 29, 0, 3, 1.77],
      [30, 34, 0, 3, 1.63],
      [35, 39, 0, 3, 1.63],
      [40, 49, 0, 3, 1.63],
      [50, 59, 0, 3, 1.60],
      [60, 999, 0, 3, 1.93],
      
      [16, 21, 3, 999, 1.87],
      [22, 24, 3, 999, 1.49],
      [25, 29, 3, 999, 1.44],
      [30, 34, 3, 999, 1.40],
      [35, 39, 3, 999, 1.34],
      [40, 49, 3, 999, 1.28],
      [50, 59, 3, 999, 1.25],
      [60, 999, 3, 999, 1.60]
    ];
    
    const numAge = parseInt(age, 10);
    const numExperience = parseInt(experience, 10);
    
    // Поиск подходящего коэффициента
    for (const [minAge, maxAge, minExp, maxExp, factor] of ageExperienceFactors) {
      if (numAge >= minAge && numAge <= maxAge && numExperience >= minExp && numExperience <= maxExp) {
        return factor;
      }
    }
    
    // По умолчанию возвращаем базовый коэффициент
    return 1.0;
  };

  // Расчет коэффициента "мультидрайв"
  const getMultiDriverFactor = () => {
    return 1.94; // Актуальный на 2025 год
  };

  // Расчет сезонного коэффициента
  const calculateSeasonalityFactor = (months) => {
    // Таблица коэффициентов по количеству месяцев использования
    const seasonalityFactors = {
      '3': 0.5,
      '4': 0.6,
      '5': 0.65,
      '6': 0.7,
      '7': 0.8,
      '8': 0.9,
      '9': 0.95,
      '10': 0.95,
      '11': 1.0,
      '12': 1.0
    };
    
    const count = months.length;
    return seasonalityFactors[count.toString()] || 1.0;
  };

  // Расчет ОСАГО
  const calculateInsurance = useCallback(() => {
    if (!validateForm()) {
      // Опционально прокрутка к первой ошибке
      const firstErrorKey = Object.keys(errors)[0];
      if (firstErrorKey) {
        const errorElement = document.getElementById(firstErrorKey);
        errorElement?.focus();
        errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
      return;
    }

    setIsCalculating(true);
    setResults(null);
    setBreakdown(null);

    // Используем setTimeout для обновления UI перед расчетами
    setTimeout(() => {
      try {
        // --- Подготавливаем входные данные ---
        const {
          carType, enginePower, regionCode, driverAge, drivingExperience,
          multiDrive, drivers, usagePeriod, hasAccidents, accidentsCount,
          hasDiscounts, kbmDiscount, hasSeasonality, seasonalityMonths, carAgeYears,
          includeAdditionalOptions, additionalOptions
        } = formData;

        // --- Расчет базового тарифа ---
        const baseRate = calculateBaseRate(carType, enginePower);
        
        // --- Расчет территориального коэффициента ---
        const territoryFactor = getRegionFactor(regionCode);
        
        // --- Расчет коэффициента возраста и стажа ---
        let ageExperienceFactor;
        
        if (multiDrive) {
          // Для мультидрайва - фиксированный коэффициент
          ageExperienceFactor = getMultiDriverFactor();
        } else {
          // Для ограниченного списка водителей - максимальный из всех водителей
          ageExperienceFactor = Math.max(
            ...drivers.map(driver => calculateAgeExperienceFactor(driver.age, driver.experience))
          );
        }
        
        // --- Расчет коэффициента бонус-малус (КБМ) ---
        const kbmFactor = hasDiscounts ? parseFloat(kbmDiscount) : 1.0;
        
        // --- Расчет коэффициента периода использования ---
        const periodFactor = hasSeasonality 
          ? calculateSeasonalityFactor(seasonalityMonths)
          : (parseInt(usagePeriod, 10) < 12 ? parseInt(usagePeriod, 10) / 12 : 1.0);
        
        // --- Расчет коэффициента на основе аварийности (повышающий) ---
        const accidentFactor = hasAccidents ? 1 + (parseInt(accidentsCount, 10) * 0.2) : 1.0;
        
        // --- Расчет коэффициента за возраст ТС ---
        let carAgeFactor = 1.0;
        const age = parseInt(carAgeYears, 10);
        if (age < 3) carAgeFactor = 1.0;
        else if (age < 7) carAgeFactor = 1.1;
        else if (age < 10) carAgeFactor = 1.2;
        else carAgeFactor = 1.3;

        // --- Расчет итоговой стоимости полиса ---
        let basePremium = baseRate * territoryFactor * ageExperienceFactor * kbmFactor * periodFactor * accidentFactor * carAgeFactor;
        
        // Округляем до целых рублей
        basePremium = Math.round(basePremium);
        
        // --- Расчет стоимости дополнительных опций ---
        let additionalPremium = 0;
        let additionalBreakdown = {};
        
        if (includeAdditionalOptions) {
          if (additionalOptions.extendedCoverage) {
            const extendedCoverageCost = Math.round(basePremium * 0.15);
            additionalPremium += extendedCoverageCost;
            additionalBreakdown.extendedCoverage = extendedCoverageCost;
          }
          
          if (additionalOptions.noDeductible) {
            const noDeductibleCost = Math.round(basePremium * 0.2);
            additionalPremium += noDeductibleCost;
            additionalBreakdown.noDeductible = noDeductibleCost;
          }
          
          if (additionalOptions.evacuationService) {
            const evacuationCost = 1200; // Фиксированная стоимость
            additionalPremium += evacuationCost;
            additionalBreakdown.evacuationService = evacuationCost;
          }
          
          if (additionalOptions.rentalCar) {
            const rentalCarCost = 3500; // Фиксированная стоимость
            additionalPremium += rentalCarCost;
            additionalBreakdown.rentalCar = rentalCarCost;
          }
          
          if (additionalOptions.remotePolicyIssue) {
            const remotePolicyCost = 500; // Фиксированная стоимость
            additionalPremium += remotePolicyCost;
            additionalBreakdown.remotePolicyIssue = remotePolicyCost;
          }
        }
        
        // Итоговая стоимость
        const totalPremium = basePremium + additionalPremium;
        
        // --- Подготовка результатов расчета ---
        const calculationResults = {
          basePremium,
          additionalPremium,
          totalPremium,
          baseRate,
          territoryFactor,
          ageExperienceFactor,
          kbmFactor,
          periodFactor,
          accidentFactor,
          carAgeFactor,
          hasAdditionalOptions: includeAdditionalOptions && additionalPremium > 0
        };
        
        // Детализация расчета для отображения
        const premiumBreakdown = {
          carType,
          enginePower,
          baseRate,
          factors: {
            territory: {
              name: 'Регион регистрации',
              value: territoryFactor,
              region: popularRegions.find(r => r.code === regionCode)?.name || `Регион ${regionCode}`
            },
            ageExperience: {
              name: multiDrive ? 'Мультидрайв' : 'Возраст и стаж',
              value: ageExperienceFactor,
              details: multiDrive ? 'Без ограничения водителей' : `${drivers.length} водител${drivers.length === 1 ? 'ь' : 'ей'}`
            },
            kbm: {
              name: 'КБМ (бонус-малус)',
              value: kbmFactor,
              details: kbmFactor < 1 ? `Скидка ${Math.round((1 - kbmFactor) * 100)}%` : kbmFactor > 1 ? `Надбавка ${Math.round((kbmFactor - 1) * 100)}%` : 'Базовый'
            },
            period: {
              name: hasSeasonality ? 'Сезонное использование' : 'Период использования',
              value: periodFactor,
              details: hasSeasonality ? `${seasonalityMonths.length} мес.` : `${usagePeriod} мес.`
            },
            accident: {
              name: 'Аварийность',
              value: accidentFactor,
              details: hasAccidents ? `${accidentsCount} ДТП` : 'Без ДТП'
            },
            carAge: {
              name: 'Возраст автомобиля',
              value: carAgeFactor,
              details: `${carAgeYears} лет`
            }
          },
          additionalOptions: includeAdditionalOptions ? additionalBreakdown : null
        };

        setResults(calculationResults);
        setBreakdown(premiumBreakdown);
      } catch (error) {
        console.error("Ошибка расчета:", error);
        setErrors({ general: `Произошла ошибка при расчете: ${error.message}. Пожалуйста, проверьте введенные данные или попробуйте позже.` });
        setResults(null);
        setBreakdown(null);
      } finally {
        setIsCalculating(false);
      }
    }, 50);
  }, [formData, validateForm, errors, setErrors, setResults, setBreakdown, setIsCalculating, popularRegions]);

  // Сброс формы
  const resetForm = () => {
    setFormData();
    setResults(null);
    setBreakdown(null);
    setErrors({});
    setSaveCalculationName('');
    setShowSaveDialog(false);
  };

  // Экспорт расчета в CSV
  const exportToCSV = () => {
    if (!results || !breakdown) {
      alert("Нет данных для экспорта. Сначала выполните расчет.");
      return;
    }

    // Определяем заголовки и данные для CSV
    const headers = ['Параметр', 'Значение', 'Описание'];
    
    // Базовые параметры
    const rows = [
      ['Базовый тариф', breakdown.baseRate.toFixed(2), `${getCarTypeName(breakdown.carType)}, ${breakdown.enginePower} л.с.`],
      ['Территориальный коэффициент', breakdown.factors.territory.value.toFixed(2), breakdown.factors.territory.region],
      ['Коэффициент возраста и стажа', breakdown.factors.ageExperience.value.toFixed(2), breakdown.factors.ageExperience.details],
      ['КБМ', breakdown.factors.kbm.value.toFixed(2), breakdown.factors.kbm.details],
      ['Коэффициент периода', breakdown.factors.period.value.toFixed(2), breakdown.factors.period.details],
      ['Коэффициент аварийности', breakdown.factors.accident.value.toFixed(2), breakdown.factors.accident.details],
      ['Коэффициент возраста ТС', breakdown.factors.carAge.value.toFixed(2), breakdown.factors.carAge.details],
      ['Базовая премия ОСАГО', results.basePremium.toFixed(2), '']
    ];
    
    // Дополнительные опции, если есть
    if (results.hasAdditionalOptions && breakdown.additionalOptions) {
      rows.push(['', '', '']);
      rows.push(['Дополнительные опции', '', '']);
      
      if (breakdown.additionalOptions.extendedCoverage) {
        rows.push(['Расширенное покрытие', breakdown.additionalOptions.extendedCoverage.toFixed(2), '']);
      }
      if (breakdown.additionalOptions.noDeductible) {
        rows.push(['Без франшизы', breakdown.additionalOptions.noDeductible.toFixed(2), '']);
      }
      if (breakdown.additionalOptions.evacuationService) {
        rows.push(['Услуги эвакуации', breakdown.additionalOptions.evacuationService.toFixed(2), '']);
      }
      if (breakdown.additionalOptions.rentalCar) {
        rows.push(['Аренда автомобиля при ремонте', breakdown.additionalOptions.rentalCar.toFixed(2), '']);
      }
      if (breakdown.additionalOptions.remotePolicyIssue) {
        rows.push(['Дистанционное оформление', breakdown.additionalOptions.remotePolicyIssue.toFixed(2), '']);
      }
      
      rows.push(['Стоимость дополнительных опций', results.additionalPremium.toFixed(2), '']);
    }
    
    // Итоговая строка
    rows.push(['', '', '']);
    rows.push(['ИТОГОВАЯ СТОИМОСТЬ', results.totalPremium.toFixed(2), 'руб.']);
    
    // Формируем CSV
    const csvContent = '\uFEFF' + [headers.join(';'), ...rows.map(row => row.join(';'))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', 'расчет_осаго.csv');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  // Обработчик сохранения расчета
  const handleSaveCalculation = () => {
    if (showSaveDialog) {
      const savedResult = saveCalculation(saveCalculationName || null);
      if (savedResult) {
        alert(`Расчет "${savedResult.name}" успешно сохранен!`);
      }
      setShowSaveDialog(false);
      setSaveCalculationName('');
    } else {
      setShowSaveDialog(true);
    }
  };

  // Вспомогательная функция для получения названия типа автомобиля
  const getCarTypeName = (typeId) => {
    const found = carTypes.find(type => type.id === typeId);
    return found ? found.name : 'автомобиля';
  };

  // Обработчик отправки формы
  const handleSubmit = (e) => {
    e.preventDefault();
    calculateInsurance();
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md border border-gray-200 dark:border-gray-700 p-6 sm:p-8 mb-8">
        <h2 className="text-2xl font-semibold mb-6 text-gray-900 dark:text-white flex items-center gap-2">
          <Shield className="w-6 h-6 text-emerald-500" />
          Параметры для расчета ОСАГО
        </h2>

        {/* Общая ошибка */}
        {errors.general && (
          <div role="alert" className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-600 rounded-md text-red-700 dark:text-red-300 text-sm">
            {errors.general}
          </div>
        )}

        {/* Информационный блок */}
        <div className="mb-6 p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg flex items-start">
          <AlertTriangle className="text-amber-500 flex-shrink-0 mt-0.5 mr-3 w-5 h-5" />
          <div>
            <h3 className="text-sm font-medium text-amber-700 dark:text-amber-300 mb-1">Важная информация о расчете</h3>
            <p className="text-xs text-amber-600 dark:text-amber-400">
              Данный калькулятор предоставляет ориентировочную стоимость полиса ОСАГО согласно тарифам 2025 года 
              и учитывает все коэффициенты, предусмотренные законодательством РФ. Точную стоимость полиса 
              можно узнать у страховых компаний, так как они могут применять дополнительные коэффициенты 
              в рамках тарифного коридора.
            </p>
          </div>
        </div>

        {/* Параметры автомобиля */}
        <CarParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          errors={errors} 
          carTypes={carTypes}
          popularRegions={popularRegions}
        />

        {/* Параметры водителей */}
        <DriverParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          handleDriverChange={handleDriverChange}
          addDriver={addDriver}
          removeDriver={removeDriver}
          errors={errors} 
        />

        {/* Дополнительные параметры */}
        <AdditionalParametersSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          handleSeasonalityChange={handleSeasonalityChange}
          errors={errors} 
          kbmOptions={kbmOptions}
        />

        {/* Дополнительные опции */}
        <AdditionalOptionsSection 
          formData={formData} 
          handleInputChange={handleInputChange} 
          handleOptionChange={handleOptionChange}
          errors={errors} 
        />

        {/* Диалог сохранения расчета */}
        {showSaveDialog && (
          <div className="mt-4 mb-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-100 dark:border-emerald-800 rounded-lg">
            <div className="flex items-center">
              <input
                type="text"
                placeholder="Введите название для сохранения"
                value={saveCalculationName}
                onChange={(e) => setSaveCalculationName(e.target.value)}
                className="form-input flex-grow mr-3"
                autoFocus
              />
              <Button
                type="button"
                onClick={handleSaveCalculation}
                primary
                size="sm"
              >
                Сохранить
              </Button>
            </div>
          </div>
        )}

        {/* Кнопки действий */}
        <div className="mt-8 flex flex-wrap gap-4">
          <Button
            type="submit"
            primary
            icon={<BarChart2 className="w-5 h-5" />}
            disabled={isCalculating || Object.keys(errors).length > 0}
            isLoading={isCalculating}
            size="lg"
          >
            {isCalculating ? 'Рассчитываем...' : 'Рассчитать ОСАГО'}
          </Button>
          <Button
            type="button"
            onClick={resetForm}
            icon={<RefreshCw className="w-5 h-5" />}
            variant="secondary"
            disabled={isCalculating}
            size="lg"
          >
            Сбросить
          </Button>
          {results && (
            <Button
              type="button"
              onClick={exportToCSV}
              icon={<Download className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Скачать расчет (CSV)
            </Button>
          )}
          {results && !showSaveDialog && (
            <Button
              type="button"
              onClick={handleSaveCalculation}
              icon={<Save className="w-5 h-5" />}
              variant="secondary"
              disabled={isCalculating}
              size="lg"
            >
              Сохранить расчет
            </Button>
          )}
        </div>
        {Object.keys(errors).length > 0 && !errors.general && (
          <p className="mt-4 text-sm text-red-600 dark:text-red-400" role="alert">Пожалуйста, исправьте ошибки в форме.</p>
        )}
      </div>
    </form>
  );
};

export default CalculationForm;